import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Clock, Plus, Trash2, Send, Save, CheckCircle2, AlertTriangle } from "lucide-react";

const statusBadge = (status, t) => {
  const map = {
    draft: ["sw-badge-neutral", "Draft"],
    submitted: ["sw-badge-pending", t("status.pending")],
    approved: ["sw-badge-approved", t("status.approved")],
    rejected: ["sw-badge-rejected", t("status.rejected")],
  };
  const [cls, lbl] = map[status] || ["sw-badge-neutral", status];
  return <span className={`sw-badge ${cls}`}>{lbl}</span>;
};

// Monday of current week
const mondayOf = (d) => {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(dt.setDate(diff));
};
const fmtDate = (d) => d.toISOString().split("T")[0];

export default function Timesheet() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costCodes, setCostCodes] = useState([]);

  const [periodStart, setPeriodStart] = useState(fmtDate(mondayOf(new Date())));
  const [periodEnd, setPeriodEnd] = useState(() => {
    const d = mondayOf(new Date());
    d.setDate(d.getDate() + 6);
    return fmtDate(d);
  });
  const [currentTs, setCurrentTs] = useState(null);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [h, p, d, cc] = await Promise.all([
      api.get("/timesheets"),
      api.get("/projects"),
      api.get("/departments"),
      api.get("/cost-codes"),
    ]);
    setHistory(h.data);
    setProjects(p.data);
    setDepartments(d.data);
    setCostCodes(cc.data.filter((c) => c.active));
    // Find existing timesheet for current period
    const existing = h.data.find((ts) => ts.period_start === periodStart && ts.period_end === periodEnd);
    if (existing) {
      setCurrentTs(existing);
      setEntries(existing.entries || []);
    } else {
      setCurrentTs(null);
      setEntries([]);
    }
  };

  useEffect(() => { load(); }, [periodStart, periodEnd]);

  const addEntry = () =>
    setEntries([...entries, { date: periodStart, project_id: "", department_id: user?.department_id || "", cost_code_id: "", hours: 0, description: "" }]);

  const updateEntry = (idx, field, value) => {
    const c = [...entries];
    c[idx] = { ...c[idx], [field]: value };
    setEntries(c);
  };
  const removeEntry = (idx) => setEntries(entries.filter((_, i) => i !== idx));

  const total = entries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  const allocByProject = projects.map((p) => ({
    project: p,
    hours: entries.filter((e) => e.project_id === p.id).reduce((s, e) => s + (parseFloat(e.hours) || 0), 0),
  })).filter((x) => x.hours > 0);

  const editable = !currentTs || currentTs.status === "draft" || currentTs.status === "rejected";

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const payload = {
        period_start: periodStart,
        period_end: periodEnd,
        entries: entries.map((e) => ({ ...e, hours: parseFloat(e.hours) || 0 })),
      };
      const { data } = await api.post("/timesheets", payload);
      setCurrentTs(data);
      setMsg("Saved as draft");
      await load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!currentTs) {
      await save();
    }
    setSaving(true);
    setMsg("");
    try {
      // Save first to make sure latest entries
      const payload = {
        period_start: periodStart,
        period_end: periodEnd,
        entries: entries.map((e) => ({ ...e, hours: parseFloat(e.hours) || 0 })),
      };
      const { data: ts } = await api.post("/timesheets", payload);
      await api.post(`/timesheets/${ts.id}/submit`);
      setMsg("Timesheet submitted for approval.");
      await load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center">
          <Clock size={22} />
        </div>
        <div>
          <h1 className="sw-heading text-[28px]">Timesheet</h1>
          <p className="text-[14px] text-[#747474]">{t("ts.subtitle")}</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="sw-card flex flex-col lg:flex-row gap-4 items-end">
        <div className="flex-1">
          <label className="sw-label">{t("ts.period_start")}</label>
          <input data-testid="ts-period-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="sw-input" />
        </div>
        <div className="flex-1">
          <label className="sw-label">{t("ts.period_end")}</label>
          <input data-testid="ts-period-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="sw-input" />
        </div>
        <div className="flex items-center gap-3 px-4 py-2 bg-[#f6f6f6] rounded-[12px] min-w-[180px]">
          <div className="flex-1">
            <div className="sw-label !mb-0">{t("common.total")}</div>
            <div className="text-[22px] font-semibold text-[#323232]">{total.toFixed(1)}h</div>
          </div>
          {currentTs && statusBadge(currentTs.status, t)}
        </div>
      </div>

      {msg && (
        <div className={`rounded-[12px] px-4 py-3 text-[13px] flex items-center gap-2 ${msg.includes("fail") || msg.includes("Cannot") ? "bg-[#e5c1c1] text-[#881a27]" : "bg-[#b8e6cd] text-[#0b430b]"}`}>
          {msg.includes("fail") || msg.includes("Cannot") ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
          {msg}
        </div>
      )}

      {/* Allocation summary */}
      {allocByProject.length > 0 && (
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">Rateio por projeto</div>
          <div className="flex flex-col gap-2">
            {allocByProject.map(({ project, hours }) => (
              <div key={project.id} className="flex items-center gap-3">
                <div className="w-[140px] text-[13px] font-medium text-[#323232]">{project.code}</div>
                <div className="flex-1 h-2 bg-[#f6f6f6] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#94a3b8] to-[#475569]" style={{ width: `${total > 0 ? (hours / total) * 100 : 0}%` }} />
                </div>
                <div className="text-[13px] text-[#747474] w-[80px] text-right">{hours.toFixed(1)}h · {total > 0 ? ((hours / total) * 100).toFixed(0) : 0}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entries */}
      <div className="flex flex-col gap-3">
        {entries.map((e, idx) => (
          <div key={idx} className="sw-card grid grid-cols-1 lg:grid-cols-[140px_1fr_1fr_1fr_100px_1fr_36px] gap-3 items-end">
            <div>
              <label className="sw-label">Data</label>
              <input disabled={!editable} type="date" min={periodStart} max={periodEnd} value={e.date} onChange={(ev) => updateEntry(idx, "date", ev.target.value)} className="sw-input" />
            </div>
            <div>
              <label className="sw-label">Projeto</label>
              <select disabled={!editable} value={e.project_id || ""} onChange={(ev) => updateEntry(idx, "project_id", ev.target.value)} className="sw-input">
                <option value="">—</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="sw-label">{t("common.department")}</label>
              <select disabled={!editable} value={e.department_id || ""} onChange={(ev) => updateEntry(idx, "department_id", ev.target.value)} className="sw-input">
                <option value="">—</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="sw-label">Cost Code</label>
              <select disabled={!editable} value={e.cost_code_id || ""} onChange={(ev) => updateEntry(idx, "cost_code_id", ev.target.value)} className="sw-input">
                <option value="">—</option>
                {costCodes.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </div>
            <div>
              <label className="sw-label">{t("common.hours")}</label>
              <input disabled={!editable} data-testid={`ts-hours-${idx}`} type="number" step="0.25" min="0" value={e.hours} onChange={(ev) => updateEntry(idx, "hours", ev.target.value)} className="sw-input" />
            </div>
            <div>
              <label className="sw-label">{t("common.description")}</label>
              <input disabled={!editable} value={e.description || ""} onChange={(ev) => updateEntry(idx, "description", ev.target.value)} className="sw-input" />
            </div>
            <button disabled={!editable} type="button" onClick={() => removeEntry(idx)} className="text-[#881a27] hover:bg-[#e5c1c1] p-2 rounded-[10px] h-fit disabled:opacity-30">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {entries.length === 0 && (
          <div className="sw-card text-center py-10 text-[#747474]">{t("ts.no_rows")}</div>
        )}
      </div>

      {editable && (
        <div className="flex flex-wrap gap-3 justify-between">
          <button data-testid="ts-add-entry" onClick={addEntry} className="sw-btn-secondary"><Plus size={14} /> Adicionar linha</button>
          <div className="flex gap-2">
            <button data-testid="ts-save" onClick={save} disabled={saving || entries.length === 0} className="sw-btn-secondary"><Save size={14} /> {t("ts.save_draft")}</button>
            <button data-testid="ts-submit" onClick={submit} disabled={saving || entries.length === 0} className="sw-btn-primary"><Send size={14} /> {t("ts.submit_approval")}</button>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h2 className="sw-heading text-[18px] mb-4">{t("ts.history")}</h2>
        {history.length === 0 ? (
          <div className="sw-card text-center py-10 text-[#747474]">{t("ts.no_periods")}</div>
        ) : (
          <div className="sw-card !p-0 overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">{t("common.period")}</th>
                  <th className="text-right px-5 py-3">{t("common.total_hours")}</th>
                  <th className="text-right px-5 py-3">Linhas</th>
                  <th className="text-left px-5 py-3">{t("common.status")}</th>
                  <th className="text-left px-5 py-3">Decidido em</th>
                </tr>
              </thead>
              <tbody>
                {history.map((ts) => (
                  <tr key={ts.id} className="border-t border-[#eeeded] hover:bg-[#f6f6f6] cursor-pointer" onClick={() => { setPeriodStart(ts.period_start); setPeriodEnd(ts.period_end); }}>
                    <td className="px-5 py-3 text-[#323232]">{ts.period_start} → {ts.period_end}</td>
                    <td className="px-5 py-3 text-right font-medium">{ts.total_hours}h</td>
                    <td className="px-5 py-3 text-right text-[#747474]">{ts.entries.length}</td>
                    <td className="px-5 py-3">{statusBadge(ts.status, t)}</td>
                    <td className="px-5 py-3 text-[#747474] text-[12px]">{ts.decided_at ? ts.decided_at.split("T")[0] : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
